import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '@/lib/api';
import { PdfViewer } from '@/components/quote-editor/PdfViewer';
import { EditorForm } from '@/components/quote-editor/EditorForm';
import type { QuoteWithDetails } from '@everplan/shared';
import type { CreateFullQuoteInput, ReplaceFullQuoteInput } from '@everplan/shared';
import type { QuoteImportResult } from '@everplan/shared';
import { mapParseResultToFormData } from '@/lib/quote-import-mapper';
import type { FormulaWarning } from '@/components/quote-editor/ChargeReviewBanner';

function mapExistingQuoteToFormData(quote: QuoteWithDetails) {
  const snapshotPackages = quote.extractionSnapshot?.packages;
  return {
    name: quote.name,
    packages: quote.packages.map((pkg, idx) => ({
      name: pkg.name,
      description: pkg.description,
      isSelected: pkg.isSelected,
      statedTotal: snapshotPackages?.[idx]?.statedTotal ?? null,
      lineItems: pkg.lineItems.map((item) => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unitCost: item.unitCost,
        section: item.section,
        notes: item.notes,
      })),
      additionalCharges: pkg.additionalCharges.map((charge) => ({
        type: charge.type,
        description: charge.description,
        amount: charge.amount,
        // Propagate formula fields so live recompute can resolve percentage
        // charges. Backend persists these per Phase 2.1/2.2.
        calcMethod: charge.calcMethod,
        rateBps: charge.rateBps,
        basis: charge.basis,
        inferenceSource: charge.inferenceSource,
        confidence: charge.confidence,
      })),
    })),
    addOns: quote.addOns.map((addOn) => ({
      name: addOn.name,
      description: addOn.description,
      quantity: addOn.quantity,
      unitCost: addOn.unitCost,
      section: addOn.section,
      notes: addOn.notes,
    })),
    additionalCharges: quote.additionalCharges.map((charge) => ({
      type: charge.type,
      description: charge.description,
      amount: charge.amount,
      calcMethod: charge.calcMethod,
      rateBps: charge.rateBps,
      basis: charge.basis,
      inferenceSource: charge.inferenceSource,
      confidence: charge.confidence,
    })),
  };
}

export default function QuoteEditorPage() {
  const { id: projectId, itemId } = useParams<{ id: string; itemId: string }>();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('quoteId');
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Navigation state from upload flow
  const navState = location.state as {
    parseResult?: QuoteImportResult;
    fileUrl?: string;
    mimeType?: string;
    fileName?: string;
  } | null;

  // Fetch existing quote if editing
  const { data: existingQuote, isLoading: isLoadingQuote } = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: async () => {
      const res = await client.api.quotes[':id'].$get({ param: { id: quoteId! } });
      if (!res.ok) throw new Error('Failed to fetch quote');
      return res.json() as Promise<QuoteWithDetails>;
    },
    enabled: !!quoteId,
  });

  // Create new quote mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateFullQuoteInput) => {
      const res = await client.api.quotes.$post({ json: data });
      if (!res.ok) throw new Error('Failed to create quote');
      return res.json();
    },
  });

  // Update existing quote mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ReplaceFullQuoteInput) => {
      const res = await client.api.quotes[':id'].$put({
        param: { id: quoteId! },
        json: data,
      });
      if (!res.ok) throw new Error('Failed to update quote');
      return res.json();
    },
  });

  const handleSave = async (data: CreateFullQuoteInput | ReplaceFullQuoteInput) => {
    if (quoteId) {
      await updateMutation.mutateAsync(data as ReplaceFullQuoteInput);
    } else {
      await createMutation.mutateAsync({
        ...(data as ReplaceFullQuoteInput),
        itemId: itemId!,
      } as CreateFullQuoteInput);
    }
    queryClient.invalidateQueries({ queryKey: ['item', itemId] });
    navigate(`/projects/${projectId}?item=${itemId}`);
  };

  // Determine initial form data
  const initialData = existingQuote
    ? mapExistingQuoteToFormData(existingQuote)
    : navState?.parseResult
      ? mapParseResultToFormData(navState.parseResult, navState.fileName)
      : undefined;

  // Pull formulaWarnings from the extraction snapshot (existing quote) or
  // from the parse-document response (nav state). Both surfaces store warnings
  // with the same shape; the type is narrowed via a local cast since
  // extractionSnapshot is typed as `unknown`.
  const formulaWarnings: FormulaWarning[] = (() => {
    if (existingQuote) {
      const snap = existingQuote.extractionSnapshot as
        | { formulaWarnings?: FormulaWarning[] }
        | null
        | undefined;
      return snap?.formulaWarnings ?? [];
    }
    if (navState?.parseResult) {
      const parsed = navState.parseResult as typeof navState.parseResult & {
        formulaWarnings?: FormulaWarning[];
      };
      return parsed.formulaWarnings ?? [];
    }
    return [];
  })();

  // Determine document source
  const documentUrl = existingQuote?.sourceDocumentUrl ?? navState?.fileUrl ?? null;
  const documentMimeType =
    existingQuote?.sourceDocumentMimeType ?? navState?.mimeType ?? null;
  const resolvedDocumentUrl = documentUrl ? `/api/files/${documentUrl}` : null;

  const hasDocument = !!resolvedDocumentUrl && !!documentMimeType;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Wait for quote data to load before rendering the form
  if (quoteId && isLoadingQuote) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading quote...</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {hasDocument && (
        <div className="w-1/2 border-r border-border">
          <PdfViewer fileUrl={resolvedDocumentUrl!} mimeType={documentMimeType!} />
        </div>
      )}
      <div className={`${hasDocument ? 'w-1/2' : 'w-full'} overflow-auto`}>
        <EditorForm
          initialData={initialData}
          sourceDocumentUrl={documentUrl}
          sourceDocumentMimeType={documentMimeType}
          onSave={handleSave}
          isSaving={isSaving}
          extractionWarnings={navState?.parseResult?.warnings}
          extractionConfidence={navState?.parseResult?.confidence}
          formulaWarnings={formulaWarnings}
        />
      </div>
    </div>
  );
}
